import type { AiExtraction } from '@/shared/ocr/ai-extraction';

export type { AiExtraction } from '@/shared/ocr/ai-extraction';

export const WORKFLOW_TYPES = {
  inventorize: 'inventorize',
  sensorInstallation: 'sensor_installation',
  meterReplacement: 'meter_replacement',
  serviceInspection: 'service_inspection',
} as const;

export type WorkflowType = (typeof WORKFLOW_TYPES)[keyof typeof WORKFLOW_TYPES];

export const CAPTURE_SESSION_STATUSES = {
  draft: 'draft',
  ready: 'ready',
  queued: 'queued',
  syncing: 'syncing',
  synced: 'synced',
  syncFailed: 'sync_failed',
  needsReview: 'needs_review',
  discarded: 'discarded',
} as const;

export type CaptureSessionStatus = (typeof CAPTURE_SESSION_STATUSES)[keyof typeof CAPTURE_SESSION_STATUSES];

export const BLOCK_STATUSES = {
  empty: 'empty',
  inProgress: 'in_progress',
  valid: 'valid',
  warning: 'warning',
  blocked: 'blocked',
} as const;

export type BlockStatus = (typeof BLOCK_STATUSES)[keyof typeof BLOCK_STATUSES];

export const VALIDATION_SEVERITIES = {
  info: 'info',
  warning: 'warning',
  blocking: 'blocking',
} as const;

export type ValidationSeverity = (typeof VALIDATION_SEVERITIES)[keyof typeof VALIDATION_SEVERITIES];

export const READINESS_STATES = {
  ready: 'ready',
  warning: 'warning',
  blocked: 'blocked',
} as const;

export type ReadinessState = (typeof READINESS_STATES)[keyof typeof READINESS_STATES];

export const PHOTO_QUALITY_STATUSES = {
  blocked: 'blocked',
  ok: 'ok',
  warning: 'warning',
} as const;

export type PhotoQualityStatus = (typeof PHOTO_QUALITY_STATUSES)[keyof typeof PHOTO_QUALITY_STATUSES];

export const PHOTO_CONTEXTS = {
  cablePath: 'cable_path',
  context: 'context',
  counter: 'counter',
  detail: 'detail',
  hardware: 'hardware',
  module: 'module',
  optoConnection: 'opto_connection',
  placementOverview: 'placement_overview',
  protocol: 'protocol',
  register: 'register',
} as const;

export interface ValidationIssue {
  code: string;
  severity: ValidationSeverity;
  message: string;
  blockId?: string;
  fieldId?: string;
}

export type PhotoContext = (typeof PHOTO_CONTEXTS)[keyof typeof PHOTO_CONTEXTS];

export interface LocalPhotoRef {
  id: string;
  sessionId: string;
  context: PhotoContext;
  registerId?: string;
  localBlobId: string;
  thumbnailBlobId?: string;
  capturedAt: string;
  width: number;
  height: number;
  sizeBytes: number;
  sha256?: string;
  quality?: PhotoQualityResult;
  uploadedRemoteId?: string;
}

export interface PhotoQualityIssue {
  code: string;
  message: string;
  severity: ValidationSeverity;
}

export interface PhotoQualityResult {
  checkedAt: string;
  issues: PhotoQualityIssue[];
  status: PhotoQualityStatus;
}

export interface CaptureSession {
  id: string;
  workflowType: WorkflowType;
  meterId?: number;
  _id?: string;
  locationId?: number;
  locationApiKey?: string;
  status: CaptureSessionStatus;
  data: Record<string, unknown>;
  blockStatuses: Record<string, BlockStatus>;
  validationErrors: ValidationIssue[];
  photos: LocalPhotoRef[];
  aiExtractions: AiExtraction[];
  createdAt: string;
  updatedAt: string;
  submittedAt?: string;
  createdBy?: string;
  submittedBy?: string;
}

export interface ReadinessSummary {
  state: ReadinessState;
  requiredBlocks: number;
  validBlocks: number;
  warningBlocks: number;
  blockedBlocks: number;
}
